import { CommonModule, formatDate } from '@angular/common';
import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { GoogleMapsModule } from '@angular/google-maps';
import { MenuItem, MessageService } from 'primeng/api';
import { BadgeModule } from 'primeng/badge';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { catchError, combineLatest, finalize, forkJoin, interval, of, Subscription } from 'rxjs';
import { SessionStorageService } from '../../services/session-storage.service';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageSearchPanelComponent } from '../../shared/page-layout/page-search-panel.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { VehicleMin } from '../vehicle/vehicle-min.model';
import { VehicleService } from '../vehicle/vehicle.service';
import {
  GpsAssetMapping,
  GpsConnector,
  GpsLocation,
  GpsLocationHistoryPoint,
  GpsProvider,
  GpsProviderDevice,
  GpsSelectOption,
  GpsTrackingSource,
  GpsVehicleEvent,
  GpsVehicleLive
} from './gps-tracking.model';
import { GpsTrackingService } from './gps-tracking.service';
import { GpsMarkerAnimator } from './gps-marker-animator';
import { gpsTruckMarkerIcon } from './gps-truck-marker';

type GpsPage = 'live' | 'vehicle' | 'history' | 'providers' | 'mappings' | 'geofences' | 'alerts' | 'reports';
type TagSeverity = 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' | undefined;

interface GpsVehicleMarker {
  key: string;
  vehicle: GpsVehicleLive;
  position: google.maps.LatLngLiteral;
  options: google.maps.MarkerOptions;
}

interface DraftAlertRule {
  name: string;
  eventType: string;
  severity: string;
  threshold?: number;
  enabled: boolean;
}

interface DraftGeofence {
  name: string;
  shapeType: string;
  assignedTo: string;
  active: boolean;
}

interface GpsVehicleOption {
  label: string;
  value: number;
  plateNumber?: string | null;
  registrationNumber?: string | null;
  source: 'Owned' | 'Rental';
}

interface ConnectorCredentials {
  positionsPath: string;
  bearerToken: string;
  apiKeyHeader: string;
  apiKeyValue: string;
}

@Component({
  selector: 'app-gps-tracking-workspace',
  standalone: true,
  templateUrl: './gps-tracking-workspace.component.html',
  styleUrls: ['./gps-tracking-workspace.component.css'],
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    GoogleMapsModule,
    BadgeModule,
    BreadcrumbModule,
    ButtonModule,
    CheckboxModule,
    DatePickerModule,
    DialogModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputNumberModule,
    InputTextModule,
    ProgressSpinnerModule,
    SelectModule,
    TableModule,
    TagModule,
    ToastModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageSearchPanelComponent,
    PageDataPanelComponent
  ]
})
export class GpsTrackingWorkspaceComponent implements OnInit, OnDestroy {
  page: GpsPage = 'live';
  breadcrumbItems: MenuItem[] = [];
  homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };

  providers: GpsProvider[] = [];
  readonly failedProviderLogoCodes = new Set<string>();
  connectors: GpsConnector[] = [];
  trackingSources: GpsTrackingSource[] = [];
  vehiclesLive: GpsVehicleLive[] = [];
  selectedVehicleLive?: GpsVehicleLive;
  selectedVehicleId?: number;
  vehicleEvents: GpsVehicleEvent[] = [];
  vehicleHistory: GpsLocationHistoryPoint[] = [];
  mappings: GpsAssetMapping[] = [];
  unmappedGpsDevices: GpsProviderDevice[] = [];
  vehicleOptions: GpsVehicleOption[] = [];
  unmappedLogilinkVehicles: GpsVehicleOption[] = [];
  /** Kept as a signal so async plate lookups immediately repaint map/list labels. */
  private readonly vehicleLabels = signal(new Map<number, string>());

  isLoading = false;
  isRefreshing = false;
  keyword = '';
  selectedSourceKey = '';
  selectedStatus = '';
  selectedConnectorId?: number;
  pollingEnabled = true;
  historyRange: Date[] = [new Date(Date.now() - 6 * 60 * 60 * 1000), new Date()];
  replayIndex = 0;
  replayPlaying = false;
  replaySpeed = 1;
  currentReplayPoint?: GpsLocationHistoryPoint;

  mapCenter: google.maps.LatLngLiteral = { lat: 24.7136, lng: 46.6753 };
  mapZoom = 6;
  mapOptions: google.maps.MapOptions = {
    mapTypeId: 'roadmap',
    zoomControl: true,
    streetViewControl: false,
    mapTypeControl: false,
    fullscreenControl: true,
    minZoom: 4,
    maxZoom: 20
  };

  connectorForm: GpsConnector = this.emptyConnectorForm();
  connectorDialogVisible = false;
  connectorCredentials: ConnectorCredentials = this.emptyConnectorCredentials();
  mappingForm: GpsAssetMapping = this.emptyMappingForm();
  mappingDialogVisible = false;
  draftGeofence: DraftGeofence = { name: '', shapeType: 'POLYGON', assignedTo: 'ALL_VEHICLES', active: true };
  draftAlertRule: DraftAlertRule = { name: '', eventType: 'GPS_SIGNAL_LOST', severity: 'HIGH', enabled: true };

  readonly statusOptions: GpsSelectOption[] = [
    { label: 'All statuses', value: '' },
    { label: 'Active location', value: 'ACTIVE_LOCATION' },
    { label: 'Stale location', value: 'STALE_LOCATION' },
    { label: 'No location', value: 'NO_LOCATION' },
    { label: 'No GPS mapping', value: 'NO_GPS_MAPPING' }
  ];

  readonly syncModeOptions: GpsSelectOption[] = [
    { label: 'Polling', value: 'POLLING' },
    { label: 'Webhook', value: 'WEBHOOK' },
    { label: 'WebSocket', value: 'WEBSOCKET' }
  ];

  readonly geofenceShapeOptions: GpsSelectOption[] = [
    { label: 'Polygon', value: 'POLYGON' },
    { label: 'Circle', value: 'CIRCLE' },
    { label: 'Rectangle', value: 'RECTANGLE' }
  ];

  readonly alertEventOptions: GpsSelectOption[] = [
    { label: 'Signal lost', value: 'GPS_SIGNAL_LOST' },
    { label: 'Signal restored', value: 'GPS_SIGNAL_RESTORED' },
    { label: 'Moving', value: 'MOVING' },
    { label: 'Stopped', value: 'STOPPED' },
    { label: 'Ignition on', value: 'IGNITION_ON' },
    { label: 'Ignition off', value: 'IGNITION_OFF' },
    { label: 'Speed above threshold', value: 'SPEEDING' },
    { label: 'Geofence enter', value: 'GEOFENCE_ENTER' },
    { label: 'Geofence exit', value: 'GEOFENCE_EXIT' }
  ];

  readonly replaySpeeds: GpsSelectOption<number>[] = [
    { label: '1x', value: 1 },
    { label: '2x', value: 2 },
    { label: '10x', value: 10 },
    { label: '100x', value: 100 }
  ];

  readonly replayAlertEvents = new Set([
    'GPS_SIGNAL_LOST',
    'GPS_SIGNAL_RESTORED',
    'GPS_SIGNAL_ACQUIRED',
    'IGNITION_ON',
    'IGNITION_OFF',
    'MOVING',
    'STOPPED'
  ]);

  private routeSub?: Subscription;
  private pollSub?: Subscription;
  private replayTimer?: ReturnType<typeof setTimeout>;
  private followSelectedMarker = false;
  private selectedMarkerContextKey?: string;
  private readonly liveMarkerAnimators = new Map<string, GpsMarkerAnimator>();
  private readonly selectedMarkerAnimator = new GpsMarkerAnimator(position => {
    if (this.followSelectedMarker) {
      this.mapCenter = position;
    }
  });
  private readonly visibilityHandler = (): void => {
    if (!document.hidden && this.pollingEnabled) {
      this.refreshLiveData(false);
    }
  };

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly gpsTrackingService: GpsTrackingService,
    private readonly vehicleService: VehicleService,
    private readonly sessionStorageService: SessionStorageService,
    private readonly messageService: MessageService
  ) {}

  ngOnInit(): void {
    document.addEventListener('visibilitychange', this.visibilityHandler);
    this.routeSub = combineLatest([this.route.paramMap, this.route.data, this.route.queryParamMap]).subscribe(([paramMap, data, queryParams]) => {
      this.page = (data['page'] as GpsPage | undefined) ?? 'live';
      this.selectedVehicleId = Number(paramMap.get('vehicleId')) || undefined;
      this.selectedSourceKey = queryParams.get('sourceKey') ?? '';
      this.resetSelectedMarkerContext(this.selectedVehicleId, this.selectedSourceKey);
      const requestedConnectorId = Number(queryParams.get('connectorId')) || undefined;
      if (requestedConnectorId) {
        this.selectedConnectorId = requestedConnectorId;
      }
      if (this.page === 'history') {
        this.historyRange = this.historyRangeFromQuery(queryParams.get('from'), queryParams.get('to'));
      }
      this.setBreadcrumbs();
      this.loadWorkspace();
      this.configurePolling();
    });
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
    this.pollSub?.unsubscribe();
    this.pauseReplay();
    this.liveMarkerAnimators.forEach(animator => animator.destroy());
    this.selectedMarkerAnimator.destroy();
    document.removeEventListener('visibilitychange', this.visibilityHandler);
  }

  loadWorkspace(): void {
    const companyId = this.companyId();
    if (!companyId) {
      this.messageService.add({ severity: 'error', summary: 'Company missing', detail: 'No company is available in session.' });
      return;
    }

    this.isLoading = true;
    forkJoin({
      providers: this.gpsTrackingService.getProviders().pipe(catchError(() => of([]))),
      connectors: this.gpsTrackingService.getConnectors(companyId).pipe(catchError(() => of([]))),
      sources: this.gpsTrackingService.getTrackingSources(companyId).pipe(catchError(() => of([])))
    }).pipe(finalize(() => this.isLoading = false)).subscribe(({ providers, connectors, sources }) => {
      this.providers = providers;
      this.connectors = connectors;
      this.trackingSources = sources;
      this.ensureSelectedConnector();
      this.loadPageData(true);
    });
  }

  loadPageData(showSpinner = true): void {
    switch (this.page) {
      case 'live':
        this.loadLiveVehicles(showSpinner);
        break;
      case 'vehicle':
        this.loadVehicleDetail(showSpinner);
        break;
      case 'history':
        this.loadVehicleHistoryReplay(showSpinner);
        break;
      case 'mappings':
        this.loadMappings(showSpinner);
        break;
      case 'providers':
        break;
      case 'geofences':
      case 'alerts':
      case 'reports':
        this.loadLiveVehicles(false);
        break;
      default:
        this.loadLiveVehicles(showSpinner);
        break;
    }
  }

  refreshLiveData(showSpinner = true): void {
    if (this.page === 'live') {
      this.loadLiveVehicles(showSpinner);
    } else if (this.page === 'vehicle') {
      this.loadVehicleDetail(showSpinner);
    } else if (this.page === 'history') {
      this.loadVehicleHistoryReplay(showSpinner);
    }
  }

  loadLiveVehicles(showSpinner = true): void {
    const companyId = this.companyId();
    if (!companyId) {
      return;
    }
    if (showSpinner) {
      this.isRefreshing = true;
    }
    this.gpsTrackingService.getCompanyVehicleLive(companyId, this.selectedSourceKey)
      .pipe(finalize(() => this.isRefreshing = false))
      .subscribe({
        next: vehicles => {
          this.vehiclesLive = vehicles;
          this.resolveVehicleLabels(vehicles.map(vehicle => vehicle.vehicleId));
          this.syncLiveMarkerAnimations(vehicles);
          this.selectedVehicleLive = this.resolveSelectedVehicle(vehicles);
          this.centerMapOnVehicle(this.selectedVehicleLive);
        },
        error: () => this.messageService.add({ severity: 'error', summary: 'GPS unavailable', detail: 'Vehicle live data could not be loaded.' })
      });
  }

  loadVehicleDetail(showSpinner = true): void {
    const companyId = this.companyId();
    const vehicleId = this.selectedVehicleId;
    if (!companyId || !vehicleId) {
      return;
    }
    if (showSpinner) {
      this.isRefreshing = true;
    }
    const from = this.historyRange?.[0];
    const to = this.historyRange?.[1];
    forkJoin({
      live: this.gpsTrackingService.getVehicleLive(companyId, vehicleId, this.selectedSourceKey),
      events: this.gpsTrackingService.getVehicleEvents(companyId, vehicleId, this.selectedSourceKey, from, to, 100),
      history: this.gpsTrackingService.getVehicleHistory(companyId, vehicleId, this.selectedSourceKey, from, to)
    }).pipe(finalize(() => this.isRefreshing = false)).subscribe({
      next: ({ live, events, history }) => {
        this.selectedVehicleLive = live;
        this.resolveVehicleLabels([vehicleId]);
        this.vehicleEvents = events;
        this.vehicleHistory = history;
        this.centerMapOnVehicle(live);
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'GPS unavailable', detail: 'Vehicle tracking detail could not be loaded.' })
    });
  }

  loadVehicleHistoryReplay(showSpinner = true): void {
    const companyId = this.companyId();
    const vehicleId = this.selectedVehicleId;
    if (!companyId || !vehicleId) {
      return;
    }
    if (showSpinner) {
      this.pauseReplay();
    }
    if (showSpinner) {
      this.isRefreshing = true;
    }
    const from = this.historyRange?.[0];
    const to = this.historyRange?.[1];
    forkJoin({
      live: this.gpsTrackingService.getVehicleLive(companyId, vehicleId, this.selectedSourceKey),
      events: this.gpsTrackingService.getVehicleEvents(companyId, vehicleId, this.selectedSourceKey, from, to, 500),
      history: this.gpsTrackingService.getVehicleHistory(companyId, vehicleId, this.selectedSourceKey, from, to)
    }).pipe(finalize(() => this.isRefreshing = false)).subscribe({
      next: ({ live, events, history }) => {
        this.selectedVehicleLive = live;
        this.resolveVehicleLabels([vehicleId]);
        this.vehicleEvents = this.sortVehicleEvents(events);
        this.vehicleHistory = this.sortHistoryPoints(history);
        if (showSpinner) {
          this.resetReplay(false);
        } else {
          this.syncReplayPointAfterHistoryRefresh();
        }
        if (!this.currentReplayPoint) {
          this.moveSelectedMarker(this.locationPosition(live?.location), false, 700, live?.location);
        }
        this.centerHistoryMap();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'GPS unavailable', detail: 'Vehicle history could not be loaded.' })
    });
  }

  loadMappings(showSpinner = true): void {
    const companyId = this.companyId();
    const connectorId = this.selectedConnectorId;
    if (!companyId) {
      this.mappings = [];
      this.unmappedGpsDevices = [];
      this.vehicleOptions = [];
      this.unmappedLogilinkVehicles = [];
      return;
    }
    if (showSpinner) {
      this.isRefreshing = true;
    }
    const agencyId = this.transportationAgencyId();
    const emptyVehicleResponse = { items: [] as VehicleMin[] };
    forkJoin({
      mappings: connectorId
        ? this.gpsTrackingService.getMappings(connectorId, companyId).pipe(catchError(() => of([])))
        : of([]),
      unmappedDevices: connectorId
        ? this.gpsTrackingService.getUnmappedDevices(connectorId, companyId).pipe(catchError(() => of([])))
        : of([]),
      ownedVehicles: agencyId
        ? this.vehicleService.findVehicleMinsByTransportationAgencyId(agencyId).pipe(catchError(() => of(emptyVehicleResponse)))
        : of(emptyVehicleResponse),
      rentalVehicles: this.vehicleService.getRentalVehicleMinsByCompanyId(companyId).pipe(catchError(() => of(emptyVehicleResponse)))
    })
      .pipe(finalize(() => this.isRefreshing = false))
      .subscribe({
        next: ({ mappings, unmappedDevices, ownedVehicles, rentalVehicles }) => {
          this.mappings = mappings;
          this.unmappedGpsDevices = unmappedDevices;
          this.vehicleOptions = this.toVehicleOptions([
            ...(ownedVehicles.items ?? []).map(vehicle => ({ vehicle, source: 'Owned' as const })),
            ...(rentalVehicles.items ?? []).map(vehicle => ({ vehicle, source: 'Rental' as const }))
          ]);
          this.refreshUnmappedLogilinkVehicles();
        },
        error: () => this.messageService.add({ severity: 'error', summary: 'Mappings unavailable', detail: 'GPS asset mappings could not be loaded.' })
      });
  }

  saveConnector(): void {
    const companyId = this.companyId();
    if (!companyId) {
      return;
    }
    const credentials = this.connectorCredentialsForSave();
    const shouldSendCredentials = Object.keys(credentials).length > 0;
    const request: GpsConnector = {
      ...this.connectorForm,
      companyId,
      active: this.selectedProviderAllowsActivation() ? this.connectorForm.active : false,
      credentials: shouldSendCredentials ? credentials : undefined
    };
    this.isRefreshing = true;
    this.gpsTrackingService.saveConnector(request)
      .pipe(finalize(() => this.isRefreshing = false))
      .subscribe({
        next: connector => {
          this.messageService.add({ severity: 'success', summary: 'Connector saved', detail: connector.displayName });
          this.connectorDialogVisible = false;
          this.resetConnectorForm();
          this.loadWorkspace();
        },
        error: error => this.messageService.add({ severity: 'error', summary: 'Connector rejected', detail: this.errorMessage(error) })
      });
  }

  editConnector(connector: GpsConnector): void {
    this.connectorForm = {
      ...connector,
      active: connector.active ?? true,
      syncMode: connector.syncMode ?? 'POLLING',
      pollingIntervalSeconds: connector.pollingIntervalSeconds ?? 60,
      credentials: undefined
    };
    this.connectorCredentials = this.emptyConnectorCredentials();
    this.connectorDialogVisible = true;
  }

  openNewConnectorDialog(providerCode?: string): void {
    this.resetConnectorForm();
    const provider = this.providers.find(item => item.providerCode === providerCode)
      ?? this.providers.find(item => this.canConfigureProvider(item));
    if (provider) {
      this.connectorForm = {
        ...this.connectorForm,
        providerCode: provider.providerCode,
        displayName: `${this.displayGpsName(provider.displayName)} connector`,
        pollingIntervalSeconds: provider.defaultPollingIntervalSeconds ?? 60
      };
    }
    this.connectorDialogVisible = true;
  }

  configureProvider(provider: GpsProvider): void {
    if (!this.canConfigureProvider(provider)) {
      return;
    }
    this.openNewConnectorDialog(provider.providerCode);
  }

  closeConnectorDialog(): void {
    this.connectorDialogVisible = false;
    this.resetConnectorForm();
  }

  resetConnectorForm(): void {
    this.connectorForm = this.emptyConnectorForm();
    this.connectorCredentials = this.emptyConnectorCredentials();
  }

  openConnectorMappings(connector: GpsConnector): void {
    if (!connector.id) {
      return;
    }
    this.router.navigate(['/fleet/gps/mappings'], { queryParams: { connectorId: connector.id } });
  }

  testConnector(connector: GpsConnector): void {
    const companyId = this.companyId();
    if (!companyId || !connector.id) {
      return;
    }
    this.gpsTrackingService.testConnector(connector.id, companyId).subscribe({
      next: result => this.messageService.add({
        severity: result.success ? 'success' : 'warn',
        summary: result.success ? 'Connector reachable' : 'Connector failed',
        detail: result.message ?? `${result.fetchedCount} locations fetched`
      }),
      error: error => this.messageService.add({ severity: 'error', summary: 'Connector test failed', detail: this.errorMessage(error) })
    });
  }

  syncConnector(connector: GpsConnector): void {
    const companyId = this.companyId();
    if (!companyId || !connector.id) {
      return;
    }
    this.gpsTrackingService.syncConnectorNow(connector.id, companyId).subscribe({
      next: updated => {
        this.messageService.add({ severity: 'success', summary: 'Sync completed', detail: updated.lastSyncMessage ?? updated.displayName });
        this.loadWorkspace();
      },
      error: error => this.messageService.add({ severity: 'error', summary: 'Sync failed', detail: this.errorMessage(error) })
    });
  }

  saveMapping(): void {
    const companyId = this.companyId();
    const connectorId = this.selectedConnectorId;
    if (!companyId || !connectorId) {
      return;
    }
    this.applySelectedVehicleToMapping();
    this.gpsTrackingService.saveMapping(connectorId, companyId, this.mappingForm).subscribe({
      next: mapping => {
        this.messageService.add({ severity: 'success', summary: 'Mapping saved', detail: mapping.plateNumber ?? String(mapping.vehicleId) });
        this.mappingDialogVisible = false;
        this.mappingForm = this.emptyMappingForm();
        this.loadMappings();
        this.loadWorkspace();
      },
      error: error => this.messageService.add({ severity: 'error', summary: 'Mapping rejected', detail: this.errorMessage(error) })
    });
  }

  deactivateMapping(mapping: GpsAssetMapping): void {
    const companyId = this.companyId();
    const connectorId = this.selectedConnectorId;
    if (!companyId || !connectorId || !mapping.id) {
      return;
    }
    this.gpsTrackingService.deactivateMapping(connectorId, companyId, mapping.id).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Mapping deactivated', detail: mapping.providerDeviceId });
        this.loadMappings();
        this.loadWorkspace();
      },
      error: error => this.messageService.add({ severity: 'error', summary: 'Mapping update failed', detail: this.errorMessage(error) })
    });
  }

  selectVehicle(vehicle: GpsVehicleLive): void {
    this.resetSelectedMarkerContext(vehicle.vehicleId, vehicle.sourceKey ?? this.selectedSourceKey);
    this.selectedVehicleLive = vehicle;
    this.selectedVehicleId = vehicle.vehicleId;
    this.centerMapOnVehicle(vehicle);
  }

  openVehicle(vehicle: GpsVehicleLive): void {
    this.router.navigate(['/fleet/gps/vehicles', vehicle.vehicleId], {
      queryParams: this.selectedSourceKey ? { sourceKey: this.selectedSourceKey } : {}
    });
  }

  openVehicleHistory(vehicle: GpsVehicleLive): void {
    this.router.navigate(['/fleet/gps/vehicles', vehicle.vehicleId, 'history'], {
      queryParams: this.selectedSourceKey ? { sourceKey: this.selectedSourceKey } : {}
    });
  }

  onSourceChange(): void {
    this.pauseReplay();
    this.resetSelectedMarkerContext(this.selectedVehicleId, this.selectedSourceKey);
    this.loadPageData();
  }

  onHistoryRangeChange(): void {
    if (this.page === 'history') {
      this.loadVehicleHistoryReplay();
      return;
    }
    this.loadVehicleDetail();
  }

  onConnectorChange(): void {
    this.loadMappings();
  }

  onMappingVehicleChange(): void {
    this.applySelectedVehicleToMapping();
    this.refreshUnmappedLogilinkVehicles();
  }

  openNewMappingDialog(): void {
    this.mappingForm = this.emptyMappingForm();
    this.mappingDialogVisible = true;
  }

  openDeviceMappingDialog(device: GpsProviderDevice): void {
    this.mappingForm = {
      ...this.emptyMappingForm(),
      providerDeviceId: device.providerDeviceId,
      providerDeviceName: device.providerDeviceName ?? '',
      active: true
    };
    this.mappingDialogVisible = true;
  }

  openVehicleMappingDialog(vehicle: GpsVehicleOption): void {
    this.mappingForm = {
      ...this.emptyMappingForm(),
      vehicleId: vehicle.value,
      plateNumber: vehicle.plateNumber ?? vehicle.registrationNumber ?? '',
      active: true
    };
    this.mappingDialogVisible = true;
  }

  closeMappingDialog(): void {
    this.mappingDialogVisible = false;
    this.mappingForm = this.emptyMappingForm();
  }

  resetMappingForm(): void {
    this.mappingForm = this.emptyMappingForm();
    this.refreshUnmappedLogilinkVehicles();
  }

  togglePolling(): void {
    this.pollingEnabled = !this.pollingEnabled;
    this.configurePolling();
  }

  displayedVehicles(): GpsVehicleLive[] {
    const keyword = this.keyword.trim().toLowerCase();
    return this.vehiclesLive.filter(vehicle => {
      if (this.selectedStatus && vehicle.liveStatus !== this.selectedStatus) {
        return false;
      }
      if (!keyword) {
        return true;
      }
      return [
        vehicle.vehicleId,
        vehicle.sourceKey,
        vehicle.location?.providerCode,
        vehicle.location?.providerDeviceId
      ].some(value => String(value ?? '').toLowerCase().includes(keyword));
    });
  }

  mapMarkers(): GpsVehicleMarker[] {
    return this.displayedVehicles()
      .map(vehicle => {
        const position = this.animatedLiveMarkerPosition(vehicle);
        return position
          ? {
              vehicle,
              position,
              key: `${vehicle.vehicleId}:${vehicle.sourceKey ?? 'best'}`,
              options: this.vehicleMarkerOptions(vehicle)
            }
          : null;
      })
      .filter((marker): marker is GpsVehicleMarker => marker !== null);
  }

  selectedVehicleMarkerOptions(): google.maps.MarkerOptions {
    return this.truckMarkerOptions(
      this.selectedMarkerAnimator.headingDegrees(),
      true,
      this.selectedVehicleLive?.liveStatus === 'STALE_LOCATION',
      30
    );
  }

  isVehicleSelected(vehicle: GpsVehicleLive): boolean {
    return this.selectedVehicleLive?.vehicleId === vehicle.vehicleId
      && (this.selectedVehicleLive?.sourceKey ?? '') === (vehicle.sourceKey ?? '');
  }

  historyPath(): google.maps.LatLngLiteral[] {
    return this.vehicleHistory
      .map(point => this.pointPosition(point))
      .filter((position): position is google.maps.LatLngLiteral => position !== null);
  }

  replayMarkerPosition(): google.maps.LatLngLiteral | null {
    return this.currentReplayPoint ? this.selectedMarkerAnimator.position() : null;
  }

  selectedLiveMarkerPosition(): google.maps.LatLngLiteral {
    return this.selectedMarkerAnimator.position()
      ?? this.locationPosition(this.selectedVehicleLive?.location)
      ?? this.mapCenter;
  }

  replayMarkerOptions(): google.maps.MarkerOptions {
    return this.truckMarkerOptions(this.selectedMarkerAnimator.headingDegrees(), true, false, 40);
  }

  startReplay(): void {
    if (!this.vehicleHistory.length) {
      return;
    }
    if (this.replayIndex >= this.vehicleHistory.length - 1) {
      this.setReplayIndex(0, true);
    }
    this.replayPlaying = true;
    this.scheduleReplayStep();
  }

  pauseReplay(): void {
    this.replayPlaying = false;
    if (this.replayTimer) {
      clearTimeout(this.replayTimer);
      this.replayTimer = undefined;
    }
  }

  resetReplay(shouldCenter = true): void {
    this.pauseReplay();
    this.setReplayIndex(0, shouldCenter);
  }

  onReplayIndexChange(value: string | number): void {
    this.pauseReplay();
    this.setReplayIndex(Number(value), true);
  }

  onReplaySpeedChange(): void {
    if (this.replayPlaying) {
      this.scheduleReplayStep();
    }
  }

  replayProgress(): number {
    if (this.vehicleHistory.length <= 1) {
      return this.vehicleHistory.length ? 100 : 0;
    }
    return Math.round((this.replayIndex / (this.vehicleHistory.length - 1)) * 100);
  }

  replayMaxIndex(): number {
    return Math.max(this.vehicleHistory.length - 1, 0);
  }

  derivedAlerts(): GpsVehicleEvent[] {
    return this.vehicleEvents.filter(event => this.replayAlertEvents.has(event.eventType));
  }

  replayTimeWindowLabel(): string {
    const from = this.historyRange?.[0];
    const to = this.historyRange?.[1];
    if (!from || !to) {
      return 'Last 24 hours';
    }
    return `${formatDate(from, 'dd MMM HH:mm', 'en-US')} - ${formatDate(to, 'dd MMM HH:mm', 'en-US')}`;
  }

  metricTotal(): number {
    return this.vehiclesLive.length;
  }

  metricActive(): number {
    return this.vehiclesLive.filter(vehicle => vehicle.liveStatus === 'ACTIVE_LOCATION').length;
  }

  metricStale(): number {
    return this.vehiclesLive.filter(vehicle => vehicle.liveStatus === 'STALE_LOCATION').length;
  }

  metricUnmapped(): number {
    return this.vehiclesLive.filter(vehicle => vehicle.liveStatus === 'NO_GPS_MAPPING').length;
  }

  gpsMetrics(): PageMetric[] {
    if (this.page === 'providers') {
      const activeConnectors = this.connectors.filter(connector => connector.active).length;
      const awaitingProviders = this.providers.filter(provider => provider.providerStatus === 'AWAITING_CONTRACT').length;
      return [
        { label: 'Catalog providers', value: this.providers.length, hint: 'Marketplace', tone: 'info' },
        { label: 'Connectors', value: this.connectors.length, hint: 'Configured', tone: 'success' },
        { label: 'Active connectors', value: activeConnectors, hint: 'Ready to sync', tone: activeConnectors ? 'success' : 'default' },
        { label: 'Awaiting contract', value: awaitingProviders, hint: 'Provider API pending', tone: awaitingProviders ? 'warn' : 'default' }
      ];
    }

    if (this.page === 'mappings') {
      const activeMappings = this.mappings.filter(mapping => mapping.active).length;
      return [
        { label: 'Mapped devices', value: this.mappings.length, hint: 'Current connector', tone: 'info' },
        { label: 'Active mappings', value: activeMappings, hint: 'Vehicle-linked', tone: activeMappings ? 'success' : 'default' },
        { label: 'Unmapped GPS devices', value: this.unmappedGpsDevices.length, hint: 'Provider-side', tone: this.unmappedGpsDevices.length ? 'warn' : 'default' },
        { label: 'Unmapped vehicles', value: this.unmappedLogilinkVehicles.length, hint: 'LogiLink fleet', tone: this.unmappedLogilinkVehicles.length ? 'warn' : 'default' }
      ];
    }

    return [
      { label: 'Tracked assets', value: this.metricTotal(), hint: 'Current view', tone: 'info' },
      { label: 'Live signal', value: this.metricActive(), hint: 'Active locations', tone: 'success' },
      { label: 'Stale signal', value: this.metricStale(), hint: 'Needs review', tone: this.metricStale() ? 'warn' : 'default' },
      { label: 'Unmapped', value: this.metricUnmapped(), hint: 'Missing source link', tone: this.metricUnmapped() ? 'danger' : 'default' }
    ];
  }

  activeFilterCount(): number {
    return [
      this.keyword.trim(),
      this.selectedSourceKey,
      this.selectedStatus,
      (this.page === 'vehicle' || this.page === 'history') && this.historyRange?.some(value => !!value) ? 'history' : ''
    ].filter(Boolean).length;
  }

  filterResultText(): string {
    if (this.page === 'providers') {
      return `${this.connectors.length} ${this.connectors.length === 1 ? 'connector' : 'connectors'}`;
    }
    if (this.page === 'mappings') {
      return `${this.mappings.length} ${this.mappings.length === 1 ? 'mapping' : 'mappings'}`;
    }
    const count = this.displayedVehicles().length;
    return `${count} ${count === 1 ? 'vehicle' : 'vehicles'}`;
  }

  clearFilters(): void {
    this.keyword = '';
    this.selectedSourceKey = '';
    this.selectedStatus = '';
    this.historyRange = this.defaultHistoryRange(this.page === 'history' ? 24 : 6);
    this.pauseReplay();
    this.loadPageData();
  }

  sourceOptions(): GpsSelectOption[] {
    return [
      { label: 'Best available', value: '' },
      ...this.trackingSources.map(source => ({
        label: this.displayGpsName(source.displayName),
        value: source.sourceKey
      }))
    ];
  }

  providerOptions(): GpsSelectOption[] {
    return this.providers.filter(provider => this.canConfigureProvider(provider)).map(provider => ({
      label: this.displayGpsName(provider.displayName),
      value: provider.providerCode
    }));
  }

  canConfigureProvider(provider: GpsProvider): boolean {
    return provider.providerStatus === 'ACTIVE' && this.providerConfigFields(provider).includes('baseUrl');
  }

  selectedProviderHasHttpConfiguration(): boolean {
    const provider = this.providers.find(item => item.providerCode === this.connectorForm.providerCode);
    return !!provider && this.canConfigureProvider(provider);
  }

  providerSetupLabel(provider: GpsProvider): string {
    if (this.canConfigureProvider(provider)) {
      return 'Configure connector';
    }
    return provider.providerStatus === 'ACTIVE' ? 'Managed source' : 'Provider access pending';
  }

  providerSetupHint(provider: GpsProvider): string {
    if (this.canConfigureProvider(provider)) {
      return 'Set the endpoint and credentials, then test and map its devices.';
    }
    if (provider.providerStatus === 'ACTIVE') {
      return 'This source is managed by its first-party application and does not need a connector.';
    }
    return 'This provider cannot be connected until its API contract is available.';
  }

  connectorOptions(): GpsSelectOption<number>[] {
    return this.connectors
      .filter(connector => !!connector.id)
      .map(connector => ({
        label: this.displayGpsName(connector.displayName),
        value: Number(connector.id)
      }));
  }

  vehicleDropdownOptions(): GpsSelectOption<number>[] {
    return this.vehicleOptions.map(vehicle => ({
      label: vehicle.label,
      value: vehicle.value
    }));
  }

  selectedConnectorName(): string {
    return this.connectorName(this.selectedConnectorId);
  }

  selectedVehicleLabel(vehicleId?: number | null): string {
    if (!vehicleId) {
      return '-';
    }
    return this.vehicleOptions.find(vehicle => vehicle.value === vehicleId)?.label
      ?? this.vehicleLabels().get(vehicleId)
      ?? `Vehicle ${vehicleId}`;
  }

  pageTitle(): string {
    switch (this.page) {
      case 'live':
        return 'GPS Control Tower';
      case 'vehicle':
        return `Vehicle ${this.selectedVehicleId ?? ''} Tracking`;
      case 'history':
        return `Vehicle ${this.selectedVehicleId ?? ''} History Replay`;
      case 'providers':
        return 'GPS Provider Marketplace';
      case 'mappings':
        return 'Device Mapping';
      case 'geofences':
        return 'Geofences';
      case 'alerts':
        return 'Alerts';
      case 'reports':
        return 'Reports';
      default:
        return 'GPS Tracking';
    }
  }

  pageSubtitle(): string {
    switch (this.page) {
      case 'live':
        return 'Live fleet position, source health, and vehicle events.';
      case 'vehicle':
        return 'Vehicle route trail, signal events, and source-specific state.';
      case 'history':
        return 'Replay GPS trail, coordinates, events, and derived alerts for a selected period.';
      case 'providers':
        return 'Connector marketplace, sync health, and provider credentials.';
      case 'mappings':
        return 'Provider asset IDs linked to LogiLink vehicles.';
      case 'geofences':
        return 'Location boundaries for fleet supervision.';
      case 'alerts':
        return 'GPS signal, movement, ignition, and exception rules.';
      case 'reports':
        return 'Operational summaries across sources, vehicles, and providers.';
      default:
        return 'Fleet GPS operations.';
    }
  }

  statusLabel(status?: string | null): string {
    switch (status) {
      case 'ACTIVE_LOCATION':
        return 'Active';
      case 'STALE_LOCATION':
        return 'Stale';
      case 'NO_GPS_MAPPING':
        return 'Unmapped';
      case 'NO_LOCATION':
        return 'No location';
      default:
        return status ?? 'Unknown';
    }
  }

  statusSeverity(status?: string | null): TagSeverity {
    switch (status) {
      case 'ACTIVE_LOCATION':
      case 'ACTIVE':
      case 'SUCCESS':
        return 'success';
      case 'STALE_LOCATION':
      case 'AWAITING_CONTRACT':
      case 'PENDING':
        return 'warn';
      case 'NO_GPS_MAPPING':
      case 'NO_LOCATION':
      case 'ERROR':
      case 'FAILED':
        return 'danger';
      case 'DISABLED':
        return 'secondary';
      default:
        return 'info';
    }
  }

  eventSeverity(eventType?: string | null): TagSeverity {
    switch (eventType) {
      case 'GPS_SIGNAL_LOST':
        return 'danger';
      case 'GPS_SIGNAL_RESTORED':
      case 'GPS_SIGNAL_ACQUIRED':
        return 'success';
      case 'MOVING':
      case 'IGNITION_ON':
        return 'info';
      case 'STOPPED':
      case 'IGNITION_OFF':
        return 'secondary';
      default:
        return 'warn';
    }
  }

  speedLabel(location?: { speedKph?: number | string | null } | null): string {
    const value = Number(location?.speedKph ?? 0);
    return `${value.toFixed(1)} kph`;
  }

  coordinateLabel(location?: { latitude?: number | string | null; longitude?: number | string | null } | null): string {
    const lat = Number(location?.latitude);
    const lng = Number(location?.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return '-';
    }
    return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
  }

  dateLabel(value?: string | null): string {
    if (!value) {
      return '-';
    }
    return formatDate(value, 'dd MMM yyyy, HH:mm', 'en-US');
  }

  providerName(providerCode?: string | null): string {
    return this.displayGpsName(this.providers.find(provider => provider.providerCode === providerCode)?.displayName ?? providerCode ?? '-');
  }

  providerLogo(providerCode?: string | null): string | null {
    if (!providerCode) {
      return null;
    }
    if (this.failedProviderLogoCodes.has(providerCode)) {
      return null;
    }
    return this.providers.find(provider => provider.providerCode === providerCode)?.logoUrl ?? null;
  }

  onProviderLogoError(providerCode?: string | null): void {
    if (providerCode) {
      this.failedProviderLogoCodes.add(providerCode);
    }
  }

  connectorName(connectorId?: number | null): string {
    return this.displayGpsName(this.connectors.find(connector => connector.id === connectorId)?.displayName ?? '-');
  }

  sourceName(sourceKey?: string | null, emptyLabel = 'best available'): string {
    if (!sourceKey) {
      return emptyLabel;
    }
    const source = this.trackingSources.find(item => item.sourceKey === sourceKey);
    return this.displayGpsName(source?.displayName ?? sourceKey);
  }

  selectedProviderAllowsActivation(): boolean {
    const provider = this.providers.find(item => item.providerCode === this.connectorForm.providerCode);
    return provider?.providerStatus === 'ACTIVE';
  }

  displayGpsName(value?: string | null): string {
    if (!value) {
      return '-';
    }
    return value
      .replace(/PROLOGIX/g, 'CARGOMATE')
      .replace(/ProLogix/g, 'CargoMate')
      .replace(/Prologix/g, 'CargoMate')
      .replace(/prologix/g, 'CargoMate');
  }

  private configurePolling(): void {
    this.pollSub?.unsubscribe();
    if (!this.pollingEnabled || (this.page !== 'live' && this.page !== 'vehicle')) {
      return;
    }
    this.pollSub = interval(15000).subscribe(() => {
      if (!document.hidden) {
        this.refreshLiveData(false);
      }
    });
  }

  private resolveSelectedVehicle(vehicles: GpsVehicleLive[]): GpsVehicleLive | undefined {
    if (this.selectedVehicleId) {
      return vehicles.find(vehicle => vehicle.vehicleId === this.selectedVehicleId) ?? vehicles[0];
    }
    return this.selectedVehicleLive
      ? vehicles.find(vehicle => vehicle.vehicleId === this.selectedVehicleLive?.vehicleId) ?? vehicles[0]
      : vehicles[0];
  }

  private centerMapOnVehicle(vehicle?: GpsVehicleLive): void {
    const position = this.locationPosition(vehicle?.location);
    if (position) {
      this.moveSelectedMarker(position, true, 700, vehicle?.location);
      this.mapZoom = this.page === 'vehicle' || this.page === 'history' ? 15 : Math.max(this.mapZoom, 7);
    }
  }

  private centerHistoryMap(): void {
    const current = this.pointPosition(this.currentReplayPoint);
    if (current) {
      this.moveSelectedMarker(current, false, 0, this.currentReplayPoint);
      this.mapCenter = current;
      this.mapZoom = 15;
      return;
    }
    this.centerMapOnVehicle(this.selectedVehicleLive);
  }

  private setReplayIndex(index: number, shouldCenter: boolean, transitionMs = 700): void {
    if (!this.vehicleHistory.length) {
      this.replayIndex = 0;
      this.currentReplayPoint = undefined;
      this.selectedMarkerAnimator.snapTo(null);
      return;
    }
    const nextIndex = Math.min(Math.max(Math.trunc(index) || 0, 0), this.vehicleHistory.length - 1);
    this.replayIndex = nextIndex;
    this.currentReplayPoint = this.vehicleHistory[nextIndex];
    const position = this.pointPosition(this.currentReplayPoint);
    this.moveSelectedMarker(position, shouldCenter, transitionMs, this.currentReplayPoint);
    if (shouldCenter && position) this.mapZoom = 15;
  }

  private scheduleReplayStep(): void {
    if (this.replayTimer) {
      clearTimeout(this.replayTimer);
      this.replayTimer = undefined;
    }
    if (!this.replayPlaying || this.replayIndex >= this.vehicleHistory.length - 1) {
      this.pauseReplay();
      return;
    }
    const delay = this.replayDelayMs(this.vehicleHistory[this.replayIndex], this.vehicleHistory[this.replayIndex + 1]);
    this.replayTimer = setTimeout(() => {
      this.setReplayIndex(this.replayIndex + 1, true, Math.min(Math.max(delay * 0.85, 80), 700));
      this.scheduleReplayStep();
    }, delay);
  }

  private animatedLiveMarkerPosition(vehicle: GpsVehicleLive): google.maps.LatLngLiteral | null {
    return this.liveMarkerAnimator(vehicle).position() ?? this.locationPosition(vehicle.location);
  }

  private syncLiveMarkerAnimations(vehicles: GpsVehicleLive[]): void {
    const activeKeys = new Set<string>();
    vehicles.forEach(vehicle => {
      activeKeys.add(this.liveMarkerKey(vehicle));
      this.liveMarkerAnimator(vehicle).moveTo(
        this.locationPosition(vehicle.location),
        vehicle.location?.headingDegrees,
        vehicle.location?.speedKph
      );
    });
    this.liveMarkerAnimators.forEach((animator, key) => {
      if (!activeKeys.has(key)) {
        animator.destroy();
        this.liveMarkerAnimators.delete(key);
      }
    });
  }

  private liveMarkerAnimator(vehicle: GpsVehicleLive): GpsMarkerAnimator {
    const key = this.liveMarkerKey(vehicle);
    let animator = this.liveMarkerAnimators.get(key);
    if (!animator) {
      animator = new GpsMarkerAnimator();
      this.liveMarkerAnimators.set(key, animator);
    }
    return animator;
  }

  private liveMarkerKey(vehicle: GpsVehicleLive): string {
    return `${vehicle.vehicleId}:${vehicle.sourceKey ?? 'best'}`;
  }

  private moveSelectedMarker(
    position: google.maps.LatLngLiteral | null,
    shouldCenter: boolean,
    transitionMs = 700,
    sample?: Pick<GpsLocation, 'headingDegrees' | 'speedKph'> | GpsLocationHistoryPoint | null
  ): void {
    this.followSelectedMarker = shouldCenter;
    this.selectedMarkerAnimator.moveTo(position, sample?.headingDegrees, sample?.speedKph, transitionMs);
  }

  private syncReplayPointAfterHistoryRefresh(): void {
    if (!this.vehicleHistory.length) {
      this.pauseReplay();
      this.setReplayIndex(0, false);
      return;
    }
    const at = this.currentReplayPoint?.recordedAt;
    const matching = at ? this.vehicleHistory.findIndex(point => point.recordedAt === at) : -1;
    this.setReplayIndex(matching >= 0 ? matching : Math.min(this.replayIndex, this.vehicleHistory.length - 1), false);
  }

  private resolveVehicleLabels(vehicleIds: number[]): void {
    const companyId = this.companyId();
    const labels = this.vehicleLabels();
    const missingIds = [...new Set(vehicleIds.filter(id => id > 0 && !labels.has(id)))];
    if (!companyId || !missingIds.length) {
      return;
    }
    this.vehicleService.findVehicleByIds('en-US', missingIds, companyId)
      .pipe(catchError(() => of(undefined)))
      .subscribe(response => {
        const updatedLabels = new Map(this.vehicleLabels());
        response?.data?.forEach(vehicle => {
          const label = vehicle.plateNumber || vehicle.registrationNumber;
          if (vehicle.vehicleId && label) {
            updatedLabels.set(vehicle.vehicleId, label);
          }
        });
        this.vehicleLabels.set(updatedLabels);
      });
  }

  private replayDelayMs(current?: GpsLocationHistoryPoint, next?: GpsLocationHistoryPoint): number {
    const fallback = 1000 / Math.max(this.replaySpeed, 1);
    const currentTime = current?.recordedAt ? new Date(current.recordedAt).getTime() : NaN;
    const nextTime = next?.recordedAt ? new Date(next.recordedAt).getTime() : NaN;
    const delta = Number.isFinite(currentTime) && Number.isFinite(nextTime) && nextTime > currentTime
      ? nextTime - currentTime
      : fallback;
    return Math.min(Math.max(delta / Math.max(this.replaySpeed, 1), 120), 2000);
  }

  private sortHistoryPoints(points: GpsLocationHistoryPoint[]): GpsLocationHistoryPoint[] {
    return [...points].sort((a, b) => this.timeValue(a.recordedAt) - this.timeValue(b.recordedAt));
  }

  private sortVehicleEvents(events: GpsVehicleEvent[]): GpsVehicleEvent[] {
    return [...events].sort((a, b) => this.timeValue(b.eventTime) - this.timeValue(a.eventTime));
  }

  private timeValue(value?: string | null): number {
    const time = value ? new Date(value).getTime() : NaN;
    return Number.isFinite(time) ? time : 0;
  }

  private historyRangeFromQuery(fromValue?: string | null, toValue?: string | null): Date[] {
    const fallback = this.defaultHistoryRange(24);
    const from = fromValue ? new Date(fromValue) : fallback[0];
    const to = toValue ? new Date(toValue) : fallback[1];
    return [
      Number.isFinite(from.getTime()) ? from : fallback[0],
      Number.isFinite(to.getTime()) ? to : fallback[1]
    ];
  }

  private defaultHistoryRange(hours: number): Date[] {
    return [new Date(Date.now() - hours * 60 * 60 * 1000), new Date()];
  }

  private locationPosition(location?: GpsLocation | null): google.maps.LatLngLiteral | null {
    const lat = Number(location?.latitude);
    const lng = Number(location?.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return null;
    }
    return { lat, lng };
  }

  pointPosition(point?: GpsLocationHistoryPoint | null): google.maps.LatLngLiteral | null {
    const lat = Number(point?.latitude);
    const lng = Number(point?.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return null;
    }
    return { lat, lng };
  }

  private vehicleMarkerOptions(vehicle?: GpsVehicleLive, forceSelected = false): google.maps.MarkerOptions {
    const isSelected = forceSelected || (!!vehicle && this.isVehicleSelected(vehicle));
    const heading = vehicle
      ? this.liveMarkerAnimators.get(this.liveMarkerKey(vehicle))?.headingDegrees() ?? vehicle.location?.headingDegrees
      : this.selectedMarkerAnimator.headingDegrees();
    return this.truckMarkerOptions(
      heading,
      isSelected,
      vehicle?.liveStatus === 'STALE_LOCATION',
      isSelected ? 30 : this.vehicleMarkerZIndex(vehicle?.liveStatus)
    );
  }

  private truckMarkerOptions(headingDegrees: number | string | null | undefined, selected: boolean, stale: boolean, zIndex: number): google.maps.MarkerOptions {
    return { icon: gpsTruckMarkerIcon({ headingDegrees, selected, stale }), zIndex, optimized: true };
  }

  private resetSelectedMarkerContext(vehicleId?: number, sourceKey?: string | null): void {
    const contextKey = `${vehicleId ?? 'none'}:${sourceKey ?? 'best'}`;
    if (this.selectedMarkerContextKey !== contextKey) {
      this.selectedMarkerContextKey = contextKey;
      this.selectedMarkerAnimator.snapTo(null);
    }
  }

  private vehicleMarkerZIndex(status?: string | null): number {
    switch (status) {
      case 'ACTIVE_LOCATION':
        return 20;
      case 'STALE_LOCATION':
        return 15;
      default:
        return 10;
    }
  }

  private toVehicleOptions(rows: Array<{ vehicle: VehicleMin; source: 'Owned' | 'Rental' }>): GpsVehicleOption[] {
    const seen = new Set<number>();
    const options: GpsVehicleOption[] = [];
    rows.forEach(row => {
      const vehicleId = Number(row.vehicle?.vehicleId);
      if (!Number.isFinite(vehicleId) || seen.has(vehicleId)) {
        return;
      }
      seen.add(vehicleId);
        const plate = row.vehicle.plateNumber || row.vehicle.registrationNumber || null;
      options.push({
          label: `${plate || 'Vehicle ' + row.vehicle.vehicleId} - ${row.source}`,
        value: vehicleId,
          plateNumber: row.vehicle.plateNumber,
          registrationNumber: row.vehicle.registrationNumber,
          source: row.source
      });
    });
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }

  private refreshUnmappedLogilinkVehicles(): void {
    const mappedVehicleIds = new Set(this.mappings
      .filter(mapping => mapping.active)
      .map(mapping => Number(mapping.vehicleId)));
    if (this.mappingDialogVisible && this.mappingForm.vehicleId) {
      mappedVehicleIds.add(Number(this.mappingForm.vehicleId));
    }
    this.unmappedLogilinkVehicles = this.vehicleOptions.filter(vehicle => !mappedVehicleIds.has(vehicle.value));
  }

  private applySelectedVehicleToMapping(): void {
    const selected = this.vehicleOptions.find(vehicle => vehicle.value === Number(this.mappingForm.vehicleId));
    if (!selected) {
      return;
    }
    this.mappingForm.plateNumber = selected.plateNumber ?? selected.registrationNumber ?? '';
  }

  private companyId(): number | undefined {
    const company = this.sessionStorageService.getCompany() ?? this.sessionStorageService.getCustomer()?.company;
    return company?.id ? Number(company.id) : undefined;
  }

  private transportationAgencyId(): number | undefined {
    const company = this.sessionStorageService.getCompany() ?? this.sessionStorageService.getCustomer()?.company;
    return company?.transportationAgencyId ? Number(company.transportationAgencyId) : undefined;
  }

  private setBreadcrumbs(): void {
    this.breadcrumbItems = [
      { label: 'Fleet', routerLink: '/fleet' },
      { label: 'GPS Tracking', routerLink: '/fleet/gps' },
      { label: this.pageTitle() }
    ];
  }

  private ensureSelectedConnector(): void {
    const isSelectedConnectorAvailable = this.connectors.some(connector => connector.id === this.selectedConnectorId);
    if (!isSelectedConnectorAvailable && this.connectors.length) {
      this.selectedConnectorId = Number(this.connectors[0].id);
    }
  }

  private emptyConnectorForm(): GpsConnector {
    return {
      companyId: 0,
      providerCode: 'GENERIC_HTTP_JSON',
      displayName: '',
      baseUrl: '',
      syncMode: 'POLLING',
      active: true,
      pollingIntervalSeconds: 60
    };
  }

  private emptyConnectorCredentials(): ConnectorCredentials {
    return { positionsPath: '', bearerToken: '', apiKeyHeader: '', apiKeyValue: '' };
  }

  private connectorCredentialsForSave(): Record<string, string> {
    return Object.entries(this.connectorCredentials).reduce<Record<string, string>>((credentials, [key, value]) => {
      const normalized = value.trim();
      if (normalized) {
        credentials[key] = normalized;
      }
      return credentials;
    }, {});
  }

  private providerConfigFields(provider: GpsProvider): string[] {
    try {
      const schema = JSON.parse(provider.configSchema ?? '{}') as { fields?: unknown };
      return Array.isArray(schema.fields) ? schema.fields.filter((field): field is string => typeof field === 'string') : [];
    } catch {
      return [];
    }
  }

  private emptyMappingForm(): GpsAssetMapping {
    return {
      providerDeviceId: '',
      providerDeviceName: '',
      vehicleId: 0,
      plateNumber: '',
      active: true
    };
  }

  private errorMessage(error: unknown): string {
    const candidate = error as { error?: { message?: string; error?: string }; message?: string };
    return candidate.error?.message ?? candidate.error?.error ?? candidate.message ?? 'Request failed.';
  }
}
